// frontend/src/components/reports/formato.ts
//
// Formateo de celda de los reports de cliente.
//
// La regla que gobierna este fichero: **`null` no es cero**. En estos reports la
// diferencia es intencionada y del cliente depende leerla bien — una cabecera de
// semielaborado sin cantidad no es «no queda nada», es «esta métrica no aplica».
// Así que el vacío se pinta como guion y el cero como cero, nunca con el mismo
// glifo.
import type { ReportCol } from './reportDefs'

const LOCALE = 'es-ES'

/**
 * Decimales por defecto de una cantidad.
 *
 * La materia prima se compra en kilos y litros, no en piezas, así que el decimal
 * ES el dato: `319,975` no es `320`. Tres bastan para lo que traen las vistas.
 */
export const DECIMALES_CANTIDAD = 3

/**
 * Decimales del consumo por unidad, que puede ser muy pequeño y necesita más
 * resolución que una cantidad normal.
 */
export const DECIMALES_CONSUMO_UNITARIO = 8

/**
 * Opciones comunes a todas las cifras de los tres reports.
 *
 * `useGrouping: 'always'` no es decorativo: es-ES agrupa por defecto en modo
 * «min2», que NO pone separador en los números de cuatro cifras. Por eso la misma
 * columna mostraba `25.000` en una fila y `5000` en la siguiente.
 */
/**
 * `Intl.NumberFormatOptions` con `useGrouping` ampliado.
 *
 * El runtime acepta 'always' | 'auto' | 'min2' desde ES2023, pero los tipos de la
 * librería de TypeScript de este proyecto todavía lo declaran como `boolean`.
 * Se extiende aquí en vez de castear en cada llamada.
 */
type OpcionesNumero = Omit<Intl.NumberFormatOptions, 'useGrouping'> & {
  useGrouping?: 'always' | 'auto' | 'min2' | boolean
}

function opciones(decimals: number): Intl.NumberFormatOptions {
  return {
    minimumFractionDigits: 0,   // se quitan los ceros a la derecha: 12.000, no 12.000,000
    maximumFractionDigits: decimals,
    useGrouping: 'always',
  } as OpcionesNumero as Intl.NumberFormatOptions
}

/** Lo que se muestra cuando el dato no aplica. */
export const NO_APLICA = '—'

/**
 * Lee un campo de una fila por su nombre.
 *
 * Los reports se pintan a partir del registro de columnas, así que el acceso es
 * necesariamente por clave dinámica. Este helper concentra la única conversión
 * de tipo que eso exige, en lugar de repartirla por los componentes, y deja las
 * interfaces de fila estrictas (sin índice de cadena que se tragaría cualquier
 * nombre mal escrito).
 */
export function campo(row: unknown, key: string): unknown {
  return (row as Record<string, unknown>)[key]
}

/**
 * La fila vista como diccionario, para el código que la recorre por clave — el
 * resumen de bloque, por ejemplo. Misma razón que `campo`: concentrar aquí la
 * conversión en lugar de repartirla, y no meter un índice de cadena en las
 * interfaces de fila.
 */
export function comoDic(row: unknown): Record<string, unknown> {
  return row as Record<string, unknown>
}

/** ¿El valor está ausente (y por tanto no aplica), en vez de valer cero? */
export function esVacio(value: unknown): boolean {
  return value == null || value === ''
}

export function fmtNum(value: unknown, decimals = DECIMALES_CANTIDAD): string {
  if (esVacio(value)) return NO_APLICA
  const n = Number(value)
  if (!Number.isFinite(n)) return NO_APLICA
  return n.toLocaleString(LOCALE, opciones(decimals))
}

export function fmtPct(value: unknown, decimals = 1): string {
  if (esVacio(value)) return NO_APLICA
  const n = Number(value)
  if (!Number.isFinite(n)) return NO_APLICA
  return `${n.toLocaleString(LOCALE, opciones(decimals))} %`
}

/** Fecha ISO (`2026-08-17` o con hora) a `17/08/2026`. */
export function fmtDate(value: unknown): string {
  if (esVacio(value)) return NO_APLICA
  const iso = String(value).slice(0, 10)
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return String(value)
  return `${d}/${m}/${y}`
}

export function fmtText(value: unknown): string {
  return esVacio(value) ? NO_APLICA : String(value)
}

/**
 * Valor de una celda ya formateado según el tipo declarado de la columna.
 *
 * Las columnas de marca (`flag`) y de recuento condicional (`count`) devuelven
 * cadena vacía cuando no hay nada que señalar: son avisos, y un aviso que se
 * pinta en todas las filas deja de avisar. Por eso NO devuelven guion.
 */
export function fmtCell(
  row: unknown,
  col: ReportCol,
  /** Formateador de código de artículo por rol. Se aplica sólo a las columnas
   *  marcadas con `esArticulo`; sin él, el código sale crudo. */
  fmtArt?: (code: string | null | undefined) => string,
): string {
  const raw = campo(row, col.key)
  if (col.esArticulo && fmtArt) return fmtArt(fmtText(raw))
  switch (col.kind) {
    case 'num':  return fmtNum(raw, col.decimals ?? DECIMALES_CANTIDAD)
    case 'pct':  return fmtPct(raw, col.decimals ?? 1)
    case 'date': return fmtDate(raw)
    case 'flag': return esVacio(raw) || Number(raw) !== col.flagOn ? '' : (col.flagText ?? '●')
    case 'count': {
      if (esVacio(raw)) return ''
      const n = Number(raw)
      if (!Number.isFinite(n) || n < (col.minToShow ?? 1)) return ''
      return fmtNum(n, col.decimals ?? 0)
    }
    default: {
      const texto = fmtText(raw)
      // El valor crudo viene de la vista, en español; el informe va en inglés.
      return col.valueLabels?.[texto] ?? texto
    }
  }
}

/** ¿Esta celda es una marca que hay que pintar como distintivo y no como texto? */
export function esMarca(col: ReportCol, row: unknown): boolean {
  return col.kind === 'flag' && Number(campo(row, col.key)) === col.flagOn
}

/**
 * Clases de una sublínea según su clase especial.
 *
 * Gris y cursiva, igual que en el Excel que ya recibe el cliente, para las tres
 * situaciones que no son un lote normal: sin lote, lote sin pedido y lote no
 * atribuido a este pedido de compra.
 */
export function claseEspecial(row: unknown): 'sinLote' | 'sinPedido' | 'noAtribuido' | null {
  if (Number(campo(row, 'esSinLote') ?? 0) === 1) return 'sinLote'
  if (Number(campo(row, 'esSinPedido') ?? 0) === 1) return 'sinPedido'
  // Ojo con el orden: esLoteAtribuido viene null en las cabeceras, donde no
  // aplica, así que solo cuenta cuando está informado y vale 0.
  const atribuido = campo(row, 'esLoteAtribuido')
  if (atribuido != null && Number(atribuido) === 0) return 'noAtribuido'
  return null
}
